import React from 'react'
import { responseIncludesAuth, getMoteLogin } from '../../../shared/Auth'
import { flattenDataFromResponse } from '@mote/helpers/identity/flattenDataFromResponse'
import {
  useAccountPropertiesMutation,
  useSurveyMutation,
  useTrackMutation
} from '@mote/api'
import { useAuth } from '../../../auth/useAuth'
import { snakify } from '@mote/helpers/misc'

export const OnboardingContext = React.createContext({})

export const OnboardingProvider = ({ children }) => {
  const loginData = React.useMemo(() => getMoteLogin(), [])

  const { updateUser } = useAuth()
  const { mutate: updateUserProperties } = useAccountPropertiesMutation()
  const { mutate: submitSurvey } = useSurveyMutation()
  const { mutate: track } = useTrackMutation()

  const getDefaultState = React.useCallback(() => {
    const state = {
      role: null,
      survey: { institutionName: '' },
      institutionNameHints: []
    }

    if (Array.isArray(loginData.institutionNameHints))
      state.institutionNameHints =
        loginData.institutionNameHints.filter(Boolean)

    return state
  }, [loginData])

  const [state, setState] = React.useState(() => getDefaultState())
  const [isLoading, setIsLoading] = React.useState(false)

  const updateState = React.useCallback(states => {
    setState(prev => ({ ...prev, ...states }))
  }, [])

  const toggleLoading = React.useCallback(() => {
    setIsLoading(prev => !prev)
  }, [])

  const resetState = React.useCallback(() => {
    setState(prev => {
      const { countryCode } = prev

      return { ...getDefaultState(), countryCode }
    })
  }, [getDefaultState])

  const updateProperties = React.useCallback(
    async ({ shouldUpdateUser = true, payload = {} }) => {
      const response = await updateUserProperties({ variables: payload })

      if (response.error) return response

      const flattenedData = flattenDataFromResponse(response.data)
      if (shouldUpdateUser && responseIncludesAuth(flattenedData)) {
        updateUser(flattenedData)
      }

      return { ...response, flattenedData }
    },
    []
  )

  const postSurvey = React.useCallback(async () => {
    const response = await submitSurvey({
      variables: { survey: snakify(state.survey) }
    })

    if (response.error) return response

    const flattenedData = flattenDataFromResponse(response.data)
    if (responseIncludesAuth(flattenedData)) {
      updateUser(flattenedData)
    }

    return { ...response, flattenedData }
  }, [state])

  const trackStep = React.useCallback(
    (key, step) => {
      const page = step === 0 ? key : `${state.role}_${key}`

      const variables = snakify({
        event: 'Survey Step Completed',
        stepNumber: step + 1,
        pageContent: page
      })

      return track({ variables })
    },
    [state]
  )

  return (
    <OnboardingContext.Provider
      value={{
        state,
        isLoading,
        updateState,
        resetState,
        setIsLoading,
        toggleLoading,
        updateProperties,
        postSurvey,
        trackStep
      }}
    >
      {children}
    </OnboardingContext.Provider>
  )
}

export const useOnboarding = () => {
  const context = React.useContext(OnboardingContext)

  if (context === null) {
    throw new Error(
      'Attempted to call useOnboarding outside the Onboarding component.'
    )
  }

  return context
}
